//Imports
import React, { useCallback, useEffect, useRef, useState } from "react";

//Controls
import { Button, Modal, Form, Select } from "@gpstrackit/gtc-react-controls";

//Components
import { useFileUpload } from 'Core/hooks';
import {
  NotificationManager,
  NotificationContainer,
} from "Components/Notifications";
import { Typography, Grid } from '@mui/material';
import FileUpload from 'Components/FileUpload';
import Table, { Column } from 'Components/Table';
import { Alert } from '@mui/material';

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//Actions
import { importEmissionCheck, downloadCsvData, loadFileCsv, getUrlFileAttachment, reset } from "./redux/slice";

const EmissionCheckImport = ({ onRefresh }) => {
  //Redux
  const dispatch = useDispatch();

  //Language
  const { messages } = useIntl();

  const formRef = useRef();
  const filesUpload = useFileUpload([]);
  const delimiters = [
    {
      id: ",",
      name: messages['emissionCheck_comma']
    },
    {
      id: ";",
      name: messages['emissionCheck_semicolon']
    }
  ];
  // State local
  const {
    loading,
    open,
    csvFile,
    urlFileCsv,
    imported,
    importResult,
    dataCsv
  } = useSelector((state) => state.emissionCheckImportRedux);

  const [delimiter, setDelimiter] = useState(delimiters[0]);
  const [fileDataCsv, setFileDataCsv] = useState([]);
  const [showMsgResult, setShowMsgResult] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    message: "",
    type: ""
  });

  const importedTotal = importResult?.items?.filter(item => item.result == 'Imported').length;

  //Handlers and utils
  const handleClose = useCallback((refresh) => {
    dispatch(reset({ open: false, result: null, loading: false, urlFileCsv: null, csvFile: null }));

    setErrorMessage({
      message: "",
      type: ""
    });

    onRefresh && onRefresh(refresh);
  });

  useEffect(() => {
    if (urlFileCsv) {
      dispatch(loadFileCsv({ Url: urlFileCsv }));
    }
  }, [urlFileCsv]);

  // download data
  useEffect(() => {
    if (dataCsv.length && downloaded) {
      let csvContent = `VIN${delimiter?.id}REGISTERED_STATE${delimiter?.id}REGISTERED_COUNTRY${delimiter?.id}REGISTRATION_EXPIRATION_DATE${delimiter?.id}ENROLLED\n`;
      for (const row of dataCsv) {
        const country = (row.registrationCountry == 'US' ? 'USA' : row.registrationCountry) || ''
        const rowContent = `${row.vin}${delimiter?.id}${row.registrationState || ''}${delimiter?.id}${country}${delimiter?.id}${row.registrationExpiration?.substr(0, 10) || ''}${delimiter?.id}TRUE\n`;
        csvContent += rowContent
      }
      let a = document.createElement('a');
      a.href = URL.createObjectURL(
        new Blob([csvContent], {
          type: 'text/csv;encoding:utf-8;charset=UTF-8',
        }),
      );
      a.setAttribute('download', `Emissions-Check-Assets-Report-${new Date().toISOString().replace('T', '_').replace(/:/g, '_').slice(0, 19)}.csv`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setDownloaded(false);
    }
  }, [dataCsv]);

  useEffect(() => {
    if (imported) {
      setShowMsgResult(true);
    }
  }, [imported]);

  useEffect(() => {
    setErrorMessage({});
    if (csvFile && open) {
      let listCARB = csvToArray(csvFile, delimiter.id);
      if (listCARB?.length && !listCARB[0]?.VIN) {
        setErrorMessage({
          message: messages['emissionCheck_error_delimiter'],
          type: "error"
        });
      } else {
        let dataCarbCSV = [];
        let errorCon = false;

        listCARB.forEach((data) => {
          if (data) {
            let error = '';
            if (!data.VIN || !data.REGISTERED_STATE || !data.REGISTRATION_EXPIRATION_DATE || !data.REGISTERED_COUNTRY || !data.ENROLLED) {
              error = messages['emissionCheck_error_data_required'];
            }

            if (!error) {
              let filterCarb = dataCarbCSV.filter((item) => item.VIN == data.VIN /*|| (item.ID && item.ID == data.ID)*/)
              if (filterCarb.length > 0)
                error = messages['emissionCheck_error_data_repeat_file'];
            }

            if (!error) {
              if (data.VIN && data.VIN?.length != 17) {
                error = messages['emissionCheck_error_vin_chars'];
              }
            }

            if (!error) {
              if (data.REGISTERED_STATE && data.REGISTERED_STATE?.length != 2) {
                error = messages['emissionCheck_error_regState_long'];
              }
            }

            if (!error) {
              if (data.REGISTERED_COUNTRY && data.REGISTERED_COUNTRY?.length != 3) {
                error = messages['emissionCheck_error_regCountry_long'];
              }
            }

            if (!error) {
              if (data.ENROLLED && !(data.ENROLLED?.trim() == 'TRUE' || data.ENROLLED?.trim() == 'FALSE')) {
                error = messages['emissionCheck_error_enrolled'];
              }
            }

            if (!error) {
              if (data.REGISTRATION_EXPIRATION_DATE && (isNaN(Date.parse(data.REGISTRATION_EXPIRATION_DATE)) || !/^\d{4}-\d{2}-\d{2}$/.test(data.REGISTRATION_EXPIRATION_DATE))) {
                error = messages['emissionCheck_error_RegExpDate'];
              }
            }

            dataCarbCSV.push({
              ...data,
              error
            });
            if (error)
              errorCon = true;
          }

        });

        setFileDataCsv(dataCarbCSV);
        if (errorCon) {
          setErrorMessage({
            message: messages['emissionCheck_error_fix'],
            type: "error"
          });
        } else {
          dispatch(importEmissionCheck({ fileKey: filesUpload?.value[0]?.fileKey, delimiter: delimiter.id }));
        }
      }
    }
  }, [csvFile, open, delimiter]);

  const UploadInfo = () => {
    if (filesUpload.value.length < 1) {
      setErrorMessage({
        message: messages['emissionCheck_error_required'],
        type: "error"
      });
      return false;
    }

    dispatch(getUrlFileAttachment({
      id: filesUpload.value[0].id
    }))
  }

  const csvToArray = (str, delimiter = ",") => {
    str = str.replace('\r', '');
    str = str.replace('(FT)', '');
    str = str.trim()
    const header_cols = str.slice(0, str.indexOf("\n")).split(delimiter);
    const row_data = str.slice(str.indexOf("\n") + 1).split("\n");
    let response = [];
    row_data.forEach((row) => {
      const values = delimiter == ',' ? row.split(/,(?![^"]*"(?:,|$))/) : row.split(delimiter);
      const cleanedData = values.map(item => item.replace(/\r/g, ''));
      let formatRow = new Object();
      header_cols.forEach((header, index) => {
        if (header && header !== '') {
          let value = (typeof cleanedData[index] !== 'undefined') ? cleanedData[index] : '';
          value = value.replace('\"', '');
          formatRow[`${header}`] = value.replace('"', '');
        }
      });
      response.push(formatRow);
    });
    return response;
  }

  return (
    <Modal
      title={messages["emissionCheck_import"]}
      size="small"
      open={open}
      handleClose={() => handleClose(true)}
      loading={loading}
      footer={
        <div>
          {!imported &&
            <Button
              variant="secondary"
              style={{ marginRight: "5px" }}
              onClick={() => handleClose()}
              isDisabled={loading}
            >
              {messages["cancel"]}
            </Button>
          }
          {fileDataCsv.length === 0 &&
            <Button
              onClick={(e) => {
                e.preventDefault();
                formRef.current.submit();
              }}
              variant="primary"
              isDisabled={loading}
            >
              {messages["emissionCheck_upload"]}
            </Button>
          }
        </div>
      }
    >
      <div style={{ padding: "10px 25px" }}>
        {errorMessage.message &&
          <Alert severity={errorMessage.type} onClose={() => { setErrorMessage("") }}>{errorMessage.message}</Alert>
        }
        <Form
          ref={formRef}
          onValidSubmit={UploadInfo}
        >
          <div style={{ padding: 24 }}>
            {fileDataCsv.length === 0 &&
              <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: '10px' }}>
                  {messages["emissionCheck_text_import_head"]}
                </Typography>

                <Button
                  type="button"
                  aria-controls="customized-menu"
                  className="btn-blue"
                  variant="contained"
                  onClick={(e) => {
                    e.preventDefault();
                    let csvContent = `VIN${delimiter?.id}REGISTERED_STATE${delimiter?.id}REGISTERED_COUNTRY${delimiter?.id}REGISTRATION_EXPIRATION_DATE${delimiter?.id}ENROLLED\n`;
                    let a = document.createElement('a');
                    a.href = URL.createObjectURL(
                      new Blob([csvContent], {
                        type: 'text/csv;encoding:utf-8;charset=UTF-8',
                      }),
                    );
                    a.setAttribute('download', 'example.csv');
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                >
                  {messages["emissionCheck_template"]}
                </Button>
                <Button
                  style={{ marginTop: '15px' }}
                  type="button"
                  aria-controls="customized-menu"
                  className="btn-blue"
                  variant="contained"
                  onClick={() => {
                    setDownloaded(true);
                    dispatch(downloadCsvData());
                  }}
                >
                  {messages["emissionCheck_template_file"]}
                </Button>

                <Table
                  data={[
                    { field: messages['emissionCheck_vin'], requirements: messages['emissionCheck_vin_requirements'] },
                    { field: messages['emissionCheck_regState'], requirements: messages['emissionCheck_regState_requirements'] },
                    { field: messages['emissionCheck_regCountry'], requirements: messages['emissionCheck_regCountry_requirements'] },
                    { field: messages['emissionCheck_regExpDate'], requirements: messages['emissionCheck_regExpDate_requirements'] },
                    { field: messages['emissionCheck_enrolled'], requirements: messages['emissionCheck_enrolled_requirements'] }
                  ]}
                  heightAuto
                  hidePagination
                  hideSelectableCheckbox
                >
                  <Column className="emissionCheck-fieldReq" label={messages['emissionCheck_field']} field="field" style={{ fontSize: 12 }} />
                  <Column label={messages['emissionCheck_requirements']} field="requirements" style={{ fontSize: 12 }} />
                </Table>

                <Typography variant="subtitle2" color="textSecondary" style={{ margin: '12px 0' }}>
                  {messages["emissionCheck_text_import_foot"]}
                </Typography>

                <Grid item sm={12} className="emissionCheck-file">
                  <FileUpload
                    {...filesUpload}
                    label={messages['emissionCheck_file']}
                    placeholder={messages['emissionCheck_file']}
                    types=".csv"
                    helpText={messages['emissionCheck_aceept_file']}
                    maxSize={3}
                    disabled={filesUpload?.value?.length > 0}
                    maxSizeMessage={`${messages['max']}: 3MB`}
                  />
                </Grid>
                <Grid item sm={12} style={{ marginTop: '20px' }}>
                  <Select
                    id="delimiter"
                    label={messages['emissionCheck_delimiter']}
                    placeholder={messages['emissionCheck_delimiter']}
                    valueField="id"
                    textField="name"
                    options={delimiters}
                    value={delimiter}
                    required
                    onChange={(data) => {
                      setDelimiter(data);
                    }}
                  />
                </Grid>
              </Grid>
            }

            {showMsgResult && imported && importResult.items?.length == 0 &&
              <Alert severity={'warning'} onClose={() => { setShowMsgResult(false) }}>{messages['emissionCheck_error_import']}</Alert>
            }

            {imported && importResult.items?.length > 0 &&
              <>
                {showMsgResult && importedTotal == 0 &&
                  <Alert severity={'error'} onClose={() => { setShowMsgResult(false) }}>{messages['emissionCheck_result_import_fail']}</Alert>
                }
                {showMsgResult && importedTotal > 0 &&
                  <Alert severity={'success'} onClose={() => { setShowMsgResult(false) }}>{messages['emissionCheck_result_import']}</Alert>
                }
                <Table
                  data={importResult.items.map(r => {
                    return { vin: r.vin, result: r.result + (r.error ? (': ' + r.error) : '') }
                  })}
                  loading={loading}
                  heightAuto
                  hidePagination
                  hideSelectableCheckbox
                >
                  <Column label={messages['emissionCheck_vin']} field="vin" style={{ fontSize: 11 }} />
                  <Column label={messages['emissionCheck_result']} field="result" style={{ fontSize: 11 }} />
                </Table>
              </>
            }

            {!imported && fileDataCsv.length > 0 &&
              <Table
                data={fileDataCsv}
                loading={loading}
                heightAuto
                hidePagination
                hideSelectableCheckbox
              >
                <Column label={messages['emissionCheck_vin']} field="VIN" style={{ fontSize: 11 }} />
                <Column label={messages['emissionCheck_error']} field="error" style={{ fontSize: 11 }} />
              </Table>
            }
          </div>
        </Form>
      </div>
      <NotificationContainer />
    </Modal>
  );
};

export default EmissionCheckImport;